import React, { useEffect, useState } from "react";
import { useAuth } from "provider/AuthContext";
import { copyText, formatToTwoDecimalPlaces } from "helper/Functions";
import { cn } from "helper/Utils";
import { useTranslation } from "react-i18next";

const UserBalance = ({ user, handleUserSettingMenuItemClick }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isBalanceHidden, setIsBalanceHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(window?.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const balanceData = [
    {
      title: t("Main Balance"),
      icon: "bc-i-circle-dollar",
      balance: user?.balance,
      bgColor: "#109121",

      buttons: [
        {
          key: "balanceManagementDeposit",
          label: t("deposit"),
          icon: "bc-i-circle-dollar",
        },
        {
          key: "balanceManagementWithdraw",
          label: t("withdraw"),
          icon: "bc-i-withdraw",
        },
      ],
    },
    {
      title: t("Total Bonus Balance"),
      icon: "bc-i-promotion",
      balance: user?.bonus_balance > 0 ? `${user?.bonus_balance}` : "00",
      bgColor: "#aa7f00",
      showFooter: true,
      footer: [
        {
          label: t("Bonus Balance"),
          value: user?.bonus_balance > 0 ? `${user?.bonus_balance}` : "00",
        },
      ],
    },
  ];

  const toggleBalanceVisibility = () => {
    setIsBalanceHidden((prev) => !prev);
  };

  return (
    <div className="flex flex-col">
      <style jsx="true">
        {`
          .menu-bg {
            background-image: radial-gradient(circle at -14% 113%, #0000004d, #0000 120%);
          }
          .main-balance-bg {
            background: radial-gradient(circle at -14% 113%, #ffffff4d, #fff0 135%);
            opacity: 0.9;
          }
          .verify-btn-shadow {
            box-shadow: 0 0 4px #ff3449, 0 0 4px #ff3449;
          }
          .verify-btn-shadow:hover {
            box-shadow: 0 0 6px #ff3449, 0 0 6px #ff3449;
            background-color: #ff344926;
          }
        `}
      </style>
      <div className="flex md:order-1 order-2 flex-col">
        <div className="flex  items-center gap-2 mb-2">
          <i className="hdr-user-avatar-icon-bc bc-i-user text-[28px] text-[rgb(255,255,255,.6)]"></i>
          <span className="text-[11px] text-[rgb(255,255,255,.5)] ml-1">{user?.c_id}</span>
          <i
            onClick={() => copyText(user?.c_id, setCopied)}
            className={cn(
              "text-[11px] cursor-pointer transition-all duration-300",
              copied ? "bc-i-checked text-[rgb(22,255,0,.35)]" : "bc-i-copy text-[rgb(255,255,255,.5)]"
            )}
          />
        </div>

        {/* <div className="mb-2 flex items-center">
          <i className="text-[#ff3449] text-[14px] w-[28px] flex items-center justify-center bc-i-not-verified"></i>
          <span className="text-[rgb(255,255,255,.9)] text-[14px] ml-3">{t("Account not verified")}</span>
        </div>

        <button className="verify-btn-shadow bg-[#ff34491a] mt-2 mb-4 min-h-[30px] text-[12px] text-[rgb(255,255,255,.9)] rounded uppercase">
          {t("Verify Now")}
        </button> */}
      </div>
      <div className="md:order-2 order-1">
        <div
          className={
            "md:p-0 px-10 grid md:grid-cols-1 md:grid-rows-2 grid-row-1 items-center auto-cols-[75vw] md:gap-3 gap-5 overflow-x-auto overflow-y-hidden hide-scrollbar scroll-smooth"
          }
          style={{
            display: "grid",
            gridAutoFlow: "column",
            WebkitOverflowScrolling: "touch", // For smooth iOS scrolling
          }}
        >
          {balanceData.map((data, index) => (
            <div
              key={index}
              className={cn("flex flex-col overflow-hidden relative main-balance-bg px-4 py-2 rounded ", index !== 0 ? "md:mt-3 md:mb-9" : "")}
              style={{ backgroundColor: data.bgColor }}
            >
              <i className={cn("absolute -right-2 -top-2 opacity-10 text-[63px]", data.icon)} />
              <div className={`flex justify-between rounded `}>
                <div className="flex flex-col leading-[1.2]">
                  <span className="text-[14px]">{data.title}</span>
                  <b className="text-[20px] font-bold mb-2">
                    {isBalanceHidden ? "*******" : `${formatToTwoDecimalPlaces(data.balance)} ${user?.currency}`}
                  </b>
                </div>
                {index === 0 && (
                  <i
                    className={`relative z-10 w-[34px] h-[34px] text-white/90 leading-[34px] text-center rounded bg-[#ffffff1a] text-[20px] u-i-p-a-c-icon-bc ${
                      isBalanceHidden ? "bc-i-eye-hidden" : "bc-i-eye"
                    } cursor-pointer`}
                    onClick={toggleBalanceVisibility}
                  />
                )}
              </div>
              {data.buttons && (
                <div className="flex justify-between gap-3 md:mt-4 md:mb-0 mt-0 mb-4">
                  {data.buttons.map((button, index) => (
                    <button
                      key={index}
                      onClick={() => handleUserSettingMenuItemClick(button)}
                      className="bg-white bg-opacity-40 hover:bg-opacity-25 flex justify-center items-center gap-2 py-[6px] w-full text-[14px] rounded"
                    >
                      <i className={`text-[18px] ${button.icon}`} />
                      {button.label}
                    </button>
                  ))}
                </div>
              )}

              {data.showFooter &&
                data.footer.map((footerItem, index) => (
                  <div key={index} className="flex justify-between items-center gap-3 border-t border-white/20 pt-2">
                    <span className="text-white/90 text-[12px]">{footerItem.label}</span>
                    <b className={cn("flex items-center h-1", isBalanceHidden ? "text-[14px]" : "text-[12px]")}>
                      {isBalanceHidden ? "*******" : formatToTwoDecimalPlaces(footerItem.value)}
                    </b>
                  </div>
                ))}
            </div>
          ))}
        </div>
        <button
          type="button"
          className="text-[rgb(255,255,255,.9)] text-[14px] py-3 px-2 menu-bg my-2.5 bg-[#b8a338] rounded w-full text-center"
          onClick={() => handleUserSettingMenuItemClick("loyaltyPoints")}
        >
          {t("Loyalty Points")}
        </button>
      </div>
    </div>
  );
};
export default UserBalance;
